"use client";

import dynamic from "next/dynamic";
import React from "react";
import { EditorSkeleton } from "./EditorSkeleton";
import { isEmptyRichTextHtml } from "../../lib/richText";

const ReactQuill: any = dynamic(() => import("react-quill-new"), {
  ssr: false,
  loading: () => <EditorSkeleton />,
});

const modules = {
  toolbar: [["bold", "italic"], [{ list: "bullet" }]],
};

const formats = ["bold", "italic", "list"];

export type RichTextEditorProps = {
  value?: string;
  defaultValue?: string;
  name?: string;
  placeholder?: string;
  required?: boolean;
  invalid?: boolean;
  className?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
};

function normalizeEditorValue(value?: string) {
  return isEmptyRichTextHtml(value) ? "" : String(value ?? "").trim();
}

export function RichTextEditor({
  value,
  defaultValue,
  name,
  placeholder,
  required,
  invalid,
  className = "",
  onChange,
  onBlur,
}: RichTextEditorProps) {
  const quillRef = React.useRef<any>(null);
  const hiddenInputRef = React.useRef<HTMLInputElement>(null);
  const [internalValue, setInternalValue] = React.useState(() => normalizeEditorValue(value ?? defaultValue));
  const isControlled = value !== undefined;
  const currentValue = normalizeEditorValue(isControlled ? value : internalValue);

  React.useEffect(() => {
    void import("react-quill-new");
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    async function attachClipboardMatcher() {
      const editor = quillRef.current?.getEditor?.();
      if (!editor || (editor as { __rfOrderedListMatcherInstalled?: boolean }).__rfOrderedListMatcherInstalled) {
        return;
      }

      const { Quill } = await import("react-quill-new");
      if (cancelled) return;

      const Delta = Quill.import("delta");
      editor.clipboard.addMatcher("OL", (_node: Node, delta: any) => {
        return delta.compose(new Delta().retain(delta.length(), { list: "bullet" }));
      });

      (editor as { __rfOrderedListMatcherInstalled?: boolean }).__rfOrderedListMatcherInstalled = true;
    }

    void attachClipboardMatcher();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = currentValue;
    }
  }, [currentValue]);

  return (
    <>
      <input ref={hiddenInputRef} type="hidden" name={name} value={currentValue} readOnly />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={currentValue}
        onChange={(nextValue) => {
          const normalized = normalizeEditorValue(nextValue);
          if (!isControlled) {
            setInternalValue(normalized);
          }
          if (hiddenInputRef.current) {
            hiddenInputRef.current.value = normalized;
            hiddenInputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
          }
          onChange?.(normalized);
        }}
        onBlur={() => {
          if (hiddenInputRef.current) {
            hiddenInputRef.current.dispatchEvent(new Event("blur", { bubbles: true }));
          }
          onBlur?.();
        }}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        preserveWhitespace
        useSemanticHTML
        readOnly={false}
        className={[className, invalid ? "rf-quill rf-quill-invalid" : "rf-quill"].filter(Boolean).join(" ")}
      />
      {required && !currentValue ? <input type="hidden" data-required-field="true" /> : null}
    </>
  );
}
